import { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import {
  loadProjectDetail,
  getProjectAgg,
  updateProject,
  deleteRedirectUrl,
  updateRedirectUrl,
  getProjectRedirectUrls,
  getProjectRedirectUrlsByDay,
  listUsersForSharedProject,
  listUsers,
  shareProject,
} from "../../service/api_service";
import moment from "moment";

import CsvDownloadButton from "react-json-to-csv";

import { isActionProject } from "../../utils/helper";
import { getProjectDomain, getProjectOriginalSubUrl, numberWithCommas, numberWithK } from "../../utils/stringFormat";
import { Box, CircularProgress, Grid, Skeleton, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useColorScheme } from "@mui/material";
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import { Constants } from "../../utils/constants";
import {
  DefaultButton,
  MainButton,
  MainDateRangePicker,
  MainSwitch,
  ProjectAddRedirectModal,
  ProjectDetailChart,
  ProjectEditModal,
  ProjectEditRedirectModal,
  ProjectInfoWidget,
  ProjectScriptModal,
  ProjectShareModal,
  ProjectUrlDeleteModal,
  SearchInput,
  SkeletonTableWidget,
  SuccessButton
} from "..";

function Project({ jobStore, authStore }) {
  jobStore.setNavigation("automated");

  const user = authStore.user;

  const { mode } = useColorScheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // "&:nth-of-type(odd)": {
    //   backgroundColor: theme.palette.action.hover
    // },
    borderBottom: `1px solid ${Constants.Colors[mode].border} !important`,
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0
    }
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${Constants.Colors[mode].border}`,
    color: Constants.Colors[mode].mutedForeground,
    fontSize: Constants.Styles.fontSize.sm,
    paddingBlock: `calc(${Constants.Styles.spacing} * 2 )`,
  }));

  const { id } = useParams();
  const [fullUrlMode, setFullUrlMode] = useState(true);

  const [projectDetail, setProjectDetail] = useState({});
  const [projectAgg, setProjectAgg] = useState(null);
  const [projectAggFiltered, setProjectAggFiltered] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showSharedModal, setShowSharedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [dataSourceForRedirect, setDataSourceForRedirect] = useState([]);
  const [pageCursorRedirect, setPageCursorRedirect] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const [visible, setVisible] = useState(false);
  const [playPause, setPlayPause] = useState([]);
  const [deleteProgress, setDeleteProgress] = useState([]);
  const [visibleForm, setVisibleForm] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRedirectRecord, setEditRedirectRecord] = useState(null);
  const [searchURL, setSearchURL] = useState("");
  const [showUrlDeleteModal, setShowUrlDeleteModal] = useState(false);


  const onStripUrlToggleChange = (value) => {
    setFullUrlMode(value);
  }

  const togglePlayPause = async (original, value) => {
    setPlayPause([...playPause, original]);
    try {
      await updateProject(id, { pause: value, original: original });
      fetchData();
    } finally {
      setPlayPause(playPause.splice(playPause.indexOf(original), 1));
    }
  };

  const onUrlEdit = async (record) => {
    setEditRedirectRecord(record);
    setEditModalOpen(true);
  };

  const onUrlEditSave = async ({ match }) => {
    try {
      const payload = {
        original: editRedirectRecord.original,
        match: match,
      };
      await updateRedirectUrl(id, payload);
      setDataSourceForRedirect([]);
      setPageCursorRedirect(null);
      fetchData();
    } finally {
      setEditModalOpen(false);
    }
  };

  const onUrlDelete = async (record) => {

    setDeleteProgress([...deleteProgress, record.original]);
    setShowUrlDeleteModal(false);

    try {
      await deleteRedirectUrl(id, { original: record.original, day_url: record.day_url });
      setDataSourceForRedirect([]);
      setPageCursorRedirect(null);
      fetchData();
    } finally {
      setDeleteProgress(
        deleteProgress.splice(deleteProgress.indexOf(record.original), 1)
      );
    }
  };

  const script = () => {
    if (projectDetail) {
      const src = `${window.location.origin}/dist/app.bundle.js`;
      // eslint-disable-next-line no-useless-escape
      const script = `<script src="${src}" class="${id}" id="redirectMapperID"><\/script>`;
      return script;
    }
    return "";
  };

  const onFinish = async ({ original, match }) => {
    await updateProject(id, {
      add_redirect: true,
      original: original,
      match: match,
    });
    fetchData();
    setVisibleForm(false);
  };

  const onCreateRedirect = async () => {
    setVisibleForm(true);
  };

  const fetchMoreRedirectsByDay = async (
    more = true,
    startDate = null,
    endDate = null,
    filterUrl = "",
  ) => {
    setLoadingMore(true);
    if (startDate === null && endDate === null) {
      startDate = moment(dateRange[0]).format("YYYY-MM-DD");
      endDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    if (pageCursorRedirect === null || !more) {
      setDataSourceForRedirect([]);
    }
    let urls;
    if (more) {
      urls = await getProjectRedirectUrlsByDay(
        id,
        100,
        pageCursorRedirect,
        startDate,
        endDate,
        !fullUrlMode,
        filterUrl,
      );
    } else {
      urls = await getProjectRedirectUrlsByDay(
        id,
        100,
        null,
        startDate,
        endDate,
        !fullUrlMode,
        filterUrl,
      );
    }
    let resp = urls.items;
    setPageCursorRedirect(urls.page_cursor);
    if (resp) {
      for (let row of resp) {
        row["redirects"] = row?.redirects ? row.redirects.toFixed(0) : 0;
      }
    } else {
      setPageCursorRedirect(null);
    }
    if (dataSourceForRedirect.length > 0 && more) {
      resp = [...dataSourceForRedirect, ...resp];
    }

    const uniqueRows = [];
    resp.forEach((row) => {
      const existingRow = uniqueRows.find(
        (r) => r.original === row.original && r.match === row.match
      );

      if (existingRow) {
        existingRow.redirects += row.redirects;
      } else {
        uniqueRows.push(row);
      }
    });
    uniqueRows.sort((a, b) => b.redirects - a.redirects);
    setDataSourceForRedirect(uniqueRows);
    setLoadingMore(false);
  };

  const setProjectAggFilter = (
    startDate,
    endDate,
    fallbackProjectAgg = null
  ) => {
    let agg = projectAgg;

    if (projectAgg == null && fallbackProjectAgg != null) {
      agg = fallbackProjectAgg;
    }

    const timestamps = [];
    const counts = [];
    agg.map((item) => {
      var timestamp = moment(item.timestamp).toDate();
      if (timestamp >= startDate.startOf("day").toDate() && timestamp <= endDate.endOf("day").toDate()) {
        timestamps.push(moment(item.timestamp));
        counts.push(item.count);
      }
    });
    return [timestamps, counts];
  };

  const handleFilter = async (date) => {
    if (date === null) {
      const endDate = moment().add((1, "day"));
      const startDate = moment().subtract(30, "days");
      console.log(startDate, endDate);
      setDateRange([startDate, endDate]);
      const projectAggFilter = setProjectAggFilter(startDate, endDate);
      await fetchMoreRedirectsByDay(
        false,
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
      );
      setProjectAggFiltered(projectAggFilter);
    } else {
      const startDate = moment(date[0]);
      const endDate = moment(date[1]);
      setDateRange([startDate, endDate]);
      const projectAggFilter = setProjectAggFilter(startDate, endDate);
      setProjectAggFiltered(projectAggFilter);
      await fetchMoreRedirectsByDay(
        false,
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
      );
    }
  };

  const fetchData = useCallback(async () => {
    const projectDetail = await loadProjectDetail(id);
    setProjectDetail(projectDetail);
    const projectAgg = await getProjectAgg(id);
    setProjectAgg(projectAgg);
    const endDate = moment().add((1, "day"));
    const startDate = moment().subtract(30, "days");

    const projectAggFilter = setProjectAggFilter(
      startDate,
      endDate,
      projectAgg
    );
    setProjectAggFiltered(projectAggFilter);
    setDateRange([startDate, endDate]);
    await fetchMoreRedirectsByDay(
      false,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
    );
    setLoading(false);

    listUsersForSharedProject(id).then((resp) => {
      setSharedUsers(resp.map((item) => item.user_id));
    });
    listUsers().then((resp) => {
      const guestUsers = resp?.filter((item) => !isActionProject(item));
      setUsers(guestUsers.map((item) => {
        return {
          label: item.user_id,
          value: item.user_id,
        }
      }));
    });
  }, [id, fullUrlMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData, fullUrlMode]);

  const handleSearch = async () => {
    const startDate = dateRange?.length > 0 ? moment(dateRange[0]).format("YYYY-MM-DD") : moment().subtract(30, "days").format("YYYY-MM-DD");
    const endDate = dateRange?.length > 1 ? moment(dateRange[1]).format("YYYY-MM-DD") : moment().add((1, "day")).format("YYYY-MM-DD");
    await fetchMoreRedirectsByDay(
      false,
      startDate,
      endDate,
      searchURL,
    );
  };

  const clearSearch = async () => {
    setSearchURL("");
    const startDate = dateRange?.length > 0 ? moment(dateRange[0]).format("YYYY-MM-DD") : moment().subtract(30, "days").format("YYYY-MM-DD");
    const endDate = dateRange?.length > 1 ? moment(dateRange[1]).format("YYYY-MM-DD") : moment().add((1, "day")).format("YYYY-MM-DD");
    await fetchMoreRedirectsByDay(
      false,
      startDate,
      endDate,
    );
  }

  const handleChange = (value) => {
    shareProject(id, value);
    setSharedUsers(value);
  };

  const onEditFinish = async (project) => {
    await updateProject(project.project_id, {
      sitemap_xml: project.sitemap_xml,
      utm_source: project.utm_source,
      utm_medium: project.utm_medium,
      utm_enabled: project.utm_enabled,
      email_report: project.email_report,
      block_urls: project.block_urls,
      font_size: project.font_size,
      font_color: project.font_color,
      background_color: project.background_color,
      redirect_message: project.redirect_message,
    });
    fetchData();
    setShowEditModal(false);
  };

  return (
    <Box>
      <ProjectShareModal
        open={showSharedModal}
        onClose={() => setShowSharedModal(false)}
        project={projectDetail}
        value={sharedUsers}
        options={users}
        loading={false}
        onChange={handleChange}
      />
      <ProjectScriptModal
        open={visible}
        onClose={() => setVisible(false)}
        script={script()}
      />
      <ProjectEditModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        project={projectDetail}
        onFinish={(project) => {
          onEditFinish(project);
        }}
      />
      <ProjectAddRedirectModal
        open={visibleForm}
        onClose={() => setVisibleForm(false)}
        onFinish={onFinish}
      />
      <ProjectEditRedirectModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onFinish={onUrlEditSave}
        original={getProjectOriginalSubUrl(editRedirectRecord?.original)}
        final={editRedirectRecord?.manual_url ? editRedirectRecord?.manual_url : editRedirectRecord?.match}
      />
      <ProjectUrlDeleteModal
        open={showUrlDeleteModal}
        onClose={() => setShowUrlDeleteModal(false)}
        onFinish={onUrlDelete}
        record={editRedirectRecord}
      />

      <Stack
        direction={"row"}
        spacing={0.5}
        alignItems={"center"}
        justifyContent={"start"}
        sx={{
          color: Constants.Colors[mode].accent,
          cursor: "pointer",
          maxWidth: 200,
        }}
        onClick={() => {
          navigate("/");
        }}
      >
        <ArrowBackOutlinedIcon sx={{
          fontSize: Constants.Styles.fontSize.sm,
        }} />
        <Typography variant="body1" sx={{
          fontSize: Constants.Styles.fontSize.sm,
          lineHeight: Constants.Styles.lineHeight.sm,
        }}>
          {"Back to Projects"}
        </Typography>
      </Stack>
      <Stack
        direction={{
          xs: "column",
          sm: "column",
          md: "row",
        }}
        alignItems={{
          xs: "start",
          sm: "center",
          md: "center",
        }}
        justifyContent={"space-between"}
        mt={"1rem"}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "column",
          }}
          spacing={0.5}
          justifyContent={"center"}
          alignItems={"start"}
        >
          <Typography variant="h5" sx={{
            fontSize: Constants.Styles.fontSize["2xl"],
            fontWeight: Constants.Styles.fontWeight.semiBold,
            lineHeight: Constants.Styles.lineHeight["2xl"],
          }}>{projectDetail.name}</Typography>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"start"} spacing={0.5}
            sx={{
              color: Constants.Colors[mode].mutedForeground,
            }}
          >
            <LanguageOutlinedIcon sx={{
              fontSize: 20,
            }} />
            <Typography variant="body2" sx={{
              fontSize: Constants.Styles.fontSize.sm,
              lineHeight: Constants.Styles.lineHeight.sm,
            }}>
              {getProjectDomain(projectDetail?.sitemap_xml)}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={{
          xs: "column",
          sm: "column",
          md: "row",
        }} alignItems={"center"} justifyContent={"center"} spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: "0.5rem", sm: 0 },
          }}
        >
          <MainDateRangePicker
            onChange={(value) => {
              handleFilter(value);
            }}
            value={dateRange}
          />
          {isActionProject(user) && <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"center"}
            sx={{
              width: { xs: "100%", sm: "auto" }
            }}
          >
            <DefaultButton
              text="Share"
              size="small"
              preIcon={<ShareOutlinedIcon />}
              onClick={() => setShowSharedModal(true)}
              fullWidth={true}
            />
            <CsvDownloadButton
              data={dataSourceForRedirect}
              filename={`URL_Mappings_${projectDetail.name}.csv`}
              style={{
                background: "transparent",
                border: "none",
              }}
            >
              <DefaultButton
                text="Export"
                size="small"
                preIcon={<FileDownloadOutlinedIcon />}
                fullWidth={true}
              />
            </CsvDownloadButton>
          </Stack>}
          {isActionProject(user) && <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"center"}
            sx={{
              width: { xs: "100%", sm: "auto" }
            }}
          >
            <DefaultButton
              text="Code"
              size="small"
              preIcon={<CodeOutlinedIcon />}
              onClick={() => setVisible(true)}
            />
            <DefaultButton
              text="Settings"
              size="small"
              preIcon={<SettingsOutlinedIcon />}
              onClick={() => setShowEditModal(true)}
            />
          </Stack>}
          <SuccessButton
            text="Add Redirect"
            size="medium"
            preIcon={<AddOutlinedIcon />}
            onClick={onCreateRedirect}
          />
        </Stack>
      </Stack>

      <Grid container spacing={2} mt={3}>
        <Grid item size={{ xs: 12, sm: 12, md: 4 }} key={"total_redirects"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Total Redirects"
            value={<Typography sx={{
              fontSize: Constants.Styles.fontSize["2xl"],
              fontWeight: Constants.Styles.fontWeight.semiBold,
            }}>{numberWithK(projectDetail?.redirect_count_month)}</Typography>}
            des={<Typography
              sx={{
                fontSize: Constants.Styles.fontSize.xs,
                color: Constants.Colors[mode].accent,
              }}
            >+8% from last period</Typography>}
            icon={<SyncAltOutlinedIcon sx={{ color: Constants.Colors[mode].accent, fontSize: "1.5rem" }} />}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 12, md: 4 }} key={"active_rules"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Active Rules"
            value={<Typography sx={{
              fontSize: Constants.Styles.fontSize["2xl"],
              fontWeight: Constants.Styles.fontWeight.semiBold,
            }}>42</Typography>}
            des={<Typography
              sx={{
                fontSize: Constants.Styles.fontSize.xs,
              }}
            >Redirect mappings</Typography>}
            icon={<LanguageOutlinedIcon sx={{ color: Constants.Colors[mode].accent, fontSize: "1.5rem" }} />}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 12, md: 4 }} key={"avg_daily"}>
          <ProjectInfoWidget
            isLoading={loading}
            title="Avg. Daily"
            value={<Typography sx={{
              fontSize: Constants.Styles.fontSize["2xl"],
              fontWeight: Constants.Styles.fontWeight.semiBold,
            }}>{numberWithCommas((projectDetail?.redirect_count_month / 30).toFixed(2))}</Typography>}
            des={<Typography
              sx={{
                fontSize: Constants.Styles.fontSize.xs,
              }}
            >Redirects per day</Typography>}
            icon={<TrendingUpOutlinedIcon sx={{ color: Constants.Colors[mode].accent, fontSize: "1.5rem" }} />}
          />
        </Grid>
      </Grid>
      {!loading &&
        (dataSourceForRedirect ? (
          <Box sx={{
            mt: "2rem",
          }}>
            {projectAggFiltered && projectAggFiltered.length > 0 && (
              <ProjectDetailChart data={projectAggFiltered} />
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={"center"}
                justifyContent={"start"}
                sx={{
                  marginBlock: "1.5rem"
                }}
                spacing={2}
              >
                <SearchInput
                  placeholder="Search URLs..."
                  value={searchURL}
                  onChange={(e) => {
                    setSearchURL(e.target.value);
                    if (!e.target.value || e.target.value === "") {
                      clearSearch();
                    }
                  }}
                  sx={{
                    minWidth: 320,
                  }}
                />
                {searchURL && <Stack
                  direction={"row"}
                  spacing={2}
                  alignItems={"center"}
                  justifyContent={"start"}
                >
                  <SuccessButton
                    text={"Search"}
                    size="medium"
                    type="button"
                    onClick={handleSearch}
                    sx={{
                      maxWidth: 120,
                    }}
                  />
                  <DefaultButton
                    text={"Clear"}
                    size="medium"
                    type="button"
                    onClick={clearSearch}
                    sx={{
                      maxWidth: 120,
                    }}
                  />
                </Stack>}
              </Stack>
              <MainSwitch
                label="Full URLs"
                value={fullUrlMode}
                onChange={onStripUrlToggleChange}
              />
            </Stack>
            <TableContainer
              sx={{
                borderRadius: "6px",
                border: `1px solid ${Constants.Colors[mode].border}`,
              }}
            >
              <Table sx={{
                minWidth: 650,
              }} aria-label="project detail table">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell sx={{
                      fontWeight: Constants.Styles.fontWeight.semiBold,
                    }}>404 Page</StyledTableCell>
                    <StyledTableCell sx={{
                      fontWeight: Constants.Styles.fontWeight.semiBold,
                    }}>Redirects</StyledTableCell>
                    <StyledTableCell sx={{
                      fontWeight: Constants.Styles.fontWeight.semiBold,
                    }}>Referrer</StyledTableCell>
                    <StyledTableCell sx={{
                      fontWeight: Constants.Styles.fontWeight.semiBold,
                    }}>Redirect To</StyledTableCell>
                    <StyledTableCell sx={{
                      fontWeight: Constants.Styles.fontWeight.semiBold,
                    }} align="right">Actions</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {dataSourceForRedirect.map((item, index) => {
                    const tooltipStyle = {
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: Constants.Colors[mode].accent,
                        color: Constants.Colors[mode].foreground,
                      }
                    };
                    const tooltipProperty = {
                      tooltip: {
                        sx: {
                          color: Constants.Colors[mode].background,
                          backgroundColor: Constants.Colors[mode].foreground,
                          fontSize: Constants.Styles.fontSize.sm,
                        },
                      },
                    };
                    const playPauseValue = item?.pause ? item.pause : false;
                    const referrer = item.referrer;
                    var renderReferrer = referrer;
                    if (!referrer || referrer === "undefined") {
                      const url = new URL(item.original);
                      if (url.searchParams.get("utm_source")) {
                        renderReferrer = url.searchParams.get("utm_source");
                      } else {
                        renderReferrer = "Direct";
                      }
                    }
                    renderReferrer = getProjectDomain(renderReferrer);
                    var redirectToStrikeThroughtText = "";
                    var redirectToCurrentText = item?.match;
                    if (item?.manual_url && item?.manual_url !== redirectToCurrentText) {
                      redirectToStrikeThroughtText = item?.match;
                      redirectToCurrentText = item?.manual_url;
                    }
                    return (
                      <StyledTableRow
                        key={`${index}-${item.original}`}
                      >
                        <StyledTableCell>
                          <Tooltip title={item.original} slotProps={{ ...tooltipProperty, }}>
                            <Typography sx={{
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: Constants.Colors[mode].foreground,
                              fontSize: Constants.Styles.fontSize.sm,
                            }}>{getProjectOriginalSubUrl(item.original)}</Typography>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell
                          sx={{
                            color: Constants.Colors[mode].foreground,
                            textAlign: "center",
                          }}
                        >
                          {numberWithCommas(item.redirects)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {renderReferrer}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title={redirectToStrikeThroughtText} slotProps={{ ...tooltipProperty, }}>
                            <Box>
                              <Typography sx={{
                                textDecoration: "line-through",
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: Constants.Colors[mode].destructive,
                                fontSize: Constants.Styles.fontSize.sm,
                              }}>{getProjectOriginalSubUrl(redirectToStrikeThroughtText)}</Typography>
                              <Typography sx={{
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: Constants.Colors[mode].accent,
                                fontSize: Constants.Styles.fontSize.sm,
                              }}>{getProjectOriginalSubUrl(redirectToCurrentText)}</Typography>
                            </Box>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"end"}>
                            <Tooltip title="Edit" slotProps={{
                              ...tooltipProperty,
                            }}>
                              <Box sx={{
                                ...tooltipStyle,
                              }}>
                                <EditOutlinedIcon sx={{
                                  fontSize: "1.2rem",
                                  cursor: "pointer",
                                }} onClick={() => onUrlEdit(item)} />
                              </Box>
                            </Tooltip>
                            {playPause.indexOf(item.original) >= 0 ? (
                              <CircularProgress size={20} />
                            ) : playPauseValue ? (
                              <Tooltip title="Pause" slotProps={{
                                ...tooltipProperty,
                              }}>
                                <Box sx={{
                                  ...tooltipStyle,
                                }}>
                                  <PlayArrowOutlinedIcon sx={{
                                    fontSize: "1.2rem",
                                    cursor: "pointer",
                                  }} onClick={() =>
                                    togglePlayPause(item.original, !playPauseValue, item)
                                  } />
                                </Box>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Activate" slotProps={{
                                ...tooltipProperty,
                              }}>
                                <Box sx={{
                                  ...tooltipStyle,
                                }}>
                                  <PauseOutlinedIcon sx={{
                                    fontSize: "1.2rem",
                                    cursor: "pointer",
                                  }} onClick={() =>
                                    togglePlayPause(item.original, !playPauseValue, item)
                                  } />
                                </Box>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete" slotProps={{
                              ...tooltipProperty,
                            }}>
                              <Box sx={{
                                ...tooltipStyle,
                              }}>
                                {deleteProgress.indexOf(item.original) >= 0 ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteForeverOutlinedIcon sx={{
                                    fontSize: "1.2rem",
                                    cursor: "pointer",
                                  }} onClick={() => {
                                    setEditRedirectRecord(item);
                                    setShowUrlDeleteModal(true);
                                  }
                                  } />
                                )}
                              </Box>
                            </Tooltip>
                          </Stack>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {pageCursorRedirect && <Stack
              sx={{ mt: "1.5rem" }}
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <MainButton
                size="small"
                text={"More..."}
                onClick={fetchMoreRedirectsByDay}
                fullWidth={false}
                isSubmitting={loadingMore}
              />
            </Stack>}
          </Box>
        ) : (
          <Typography sx={{ fontSize: Constants.Styles.fontSize.base, mt: 2, }}>
            There are no redirect logs yet. Please check back in a few days.
          </Typography>
        ))}
      {(loading) && <Box
        sx={{
          mt: "2rem"
        }}
      >
        <Box>
          <Skeleton
            variant="rounded"
            height={260}
            width="100%"
            sx={{
              mb: "1.5rem",
            }}
          />
        </Box>
        <SkeletonTableWidget />
      </Box>
      }
    </Box>
  );
}

export default observer(Project);
